import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InfiniteScrollTableWrapper } from 'shared/components/InfiniteScrollTableWrapper';
import { BellMessage } from 'shared/components/BellMessage';
import { TableProps } from 'antd/lib/table';
import {
  CallTask,
  CallTaskStatus,
  CompletedCallTaskStates,
  HubspotOrgIntegrationData,
  InProgressCallTaskStates,
  OutboundCallReport,
  SourceType,
} from 'types';
import { Icon, IconName } from 'shared/components/Icon';
import { Button, Collapse, Popover, Table } from 'antd';
import './style.less';
import { Switch } from 'shared/components/Switch';
import { Tooltip } from 'shared/components/Tooltip';
import { useSelector } from 'react-redux';
import {
  selectActiveOrgIntegrations,
  selectActiveOrgMembers,
} from 'shared/store/auth-slice';
import { LBBContactPhoneNumber, OrgIntegrationService } from 'sb-lib/types';
import { CallTaskTooltip } from 'browser/components/Outbound/CallTaskDisplay/CalltaskTooltip';
import { AppState } from 'shared/store/reducers';
import clsx from 'clsx';
import { useUpdateUserOutboundSettings } from 'browser/components/Settings/Dialer/useUpdateUserOutboundSettings';
import { defaultDialerColumns } from '../CallTaskDisplayColumns/CallTaskDisplayColumnsPopover';
import { formatDateShort } from 'shared/utils/helpers';
import { SortOrder } from 'antd/lib/table/interface';
import { AutoSizer } from 'react-virtualized';
import { CallTaskDisplaySkeleton } from './CallTaskDisplaySkeleton';
import { format, utcToZonedTime } from 'date-fns-tz';
import { useAppSelector } from 'shared/store';
import { selectFilteredCallTasks } from 'shared/store/outbound-slice';
import { Property } from 'types/property';
import {
  getCompanyProperties,
  getContactProperties,
  getOutboundContactsLastCallReport,
} from 'shared/api';
import { startsWith } from 'lodash';
import {
  CallServiceName,
  getExternalContactUrl,
  getExternalServiceIconName,
} from 'browser/components/Outbound/helpers';
import {
  getActivityCallDisposition,
  getCallStatusFriendlyName,
  getPhoneNumberIcon,
} from 'shared/utils/outbound';

interface CallTaskDisplayProps {
  isDialing: boolean;
  loadMoreData: () => Promise<any>;
  onSingleDialClick: (task: CallTask) => Promise<void>;
  onInvalidNumberClick: (task: CallTask, number: string) => Promise<void>;
  onStop: () => Promise<void>;
  toggleSkipped: (task: CallTask) => Promise<void>;
}

interface BadgeOptions {
  color: string;
  border?: string;
  rank: number;
  icon?: IconName;
  invert?: boolean;
  tooltip?: string;
}

const revenueFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const statusOptions: Record<string, BadgeOptions> = {
  [CallTaskStatus.CANCELED]: {
    color: '#8E95A4',
    rank: 1,
  },
  [CallTaskStatus.FAILED]: {
    color: '#FF6F40',
    rank: 1,
  },
  [CallTaskStatus.NO_NUMBER]: {
    color: '#8E95A4',
    rank: 1,
  },
  [CallTaskStatus.ERROR]: {
    color: '#FF6F40',
    rank: 1,
  },
  [CallTaskStatus.BAD_NUMBER]: {
    color: '#FF6F40',
    rank: 1,
  },
  [CallTaskStatus.RETRY]: {
    color: '#D99E00',
    rank: 3,
  },
  [CallTaskStatus.LEFT_VOICEMAIL]: {
    color: '#2C2D36',
    rank: 3,
  },
  [CallTaskStatus.BUSY]: {
    color: '#D99E00',
    rank: 3,
  },
  [CallTaskStatus.PENDING]: {
    color: '#F2F4F7',
    border: '#E7EAF2',
    rank: 5,
    invert: true,
  },
  [CallTaskStatus.INITIATED]: {
    color: '#4068FF',
    rank: 6,
  },
  [CallTaskStatus.DIALING]: {
    color: '#4068FF',
    rank: 6,
    tooltip: 'Connection is being made.',
  },
  [CallTaskStatus.RINGING]: {
    color: '#4068FF',
    rank: 7,
    tooltip: 'A connection has been made. Ringing callee.',
  },
  // There's a lot of nuance around what's happening when something is
  // in the In Progress state as every call that is picked up by machine
  // or human is marked as In Progress. We explicitly send "Answered"
  // when a Human answers so for now, treat this like it's still ringing
  // so the Frontend call state transitions make sense, as we're not
  // doing any special handling around voicemail bots.
  [CallTaskStatus.IN_PROGRESS]: {
    color: '#4068FF',
    rank: 7,
  },
  [CallTaskStatus.ANSWERED]: {
    color: '#00BB4B',
    rank: 8,
  },
  [CallTaskStatus.NO_ANSWER]: {
    color: '#565A66',
    rank: 2,
  },
  [CallTaskStatus.COMPLETED]: {
    color: '#00BB4B',
    rank: 4,
  },
  [CallTaskStatus.SKIPPED]: {
    color: '#565A66',
    icon: 'arrowRight',
    rank: 1,
  },
};

const CallTaskSocialItem: React.FC<{ task: CallTask; serviceName: CallServiceName }> = ({
  task,
  serviceName,
}) => {
  const orgIntegrations = useSelector(selectActiveOrgIntegrations);
  const url = getExternalContactUrl(task, serviceName, orgIntegrations);
  if (url === null) {
    return null;
  }

  return (
    <a
      href={url}
      rel="noreferrer"
      className="CallTaskDisplay__socials-item"
      target="_blank"
    >
      <Icon width={12} height={12} icon={getExternalServiceIconName(serviceName)} />
    </a>
  );
};

export const CallTaskDisplay: React.FC<CallTaskDisplayProps> = ({
  isDialing,
  loadMoreData,
  onSingleDialClick,
  onInvalidNumberClick,
  onStop,
  toggleSkipped,
}) => {
  const {
    settings: { outboundDialerColumns },
  } = useUpdateUserOutboundSettings();

  const { liveTranscriptions: transcriptions } = useAppSelector(s => s.transcription);

  const columnsToRender = useMemo(
    () => (outboundDialerColumns?.length ? outboundDialerColumns : defaultDialerColumns),
    [outboundDialerColumns],
  );
  const activeUsers = useAppSelector(selectActiveOrgMembers);
  const [completedOpen, setCompletedOpen] = useState<boolean>(false);
  const [lastCallReports, setLastCallReports] = useState<
    Record<number, OutboundCallReport>
  >({});
  const [loading, setLoading] = useState<boolean>(false);
  const orgIntegrations = useSelector(selectActiveOrgIntegrations);
  const { cursor, sourceType, isLoadingTargets: isLoading } = useSelector(
    (state: AppState) => state.outbound,
  );
  const callTasks = useAppSelector(selectFilteredCallTasks);
  useEffect(() => {
    let active = true;
    load();
    return () => {
      active = false;
    };

    async function load() {
      if (!callTasks.length) {
        setLastCallReports({});
        return;
      }

      if (!active) {
        return;
      }

      const contactIds = callTasks.map(t => t.contact?.id).filter(id => id);
      const callReports = Object.keys(lastCallReports)
        .map(k => parseInt(k, 10))
        .filter(k => contactIds.includes(k))
        .reduce((obj, key) => {
          obj[key] = lastCallReports[key];
          return obj;
        }, {} as Record<number, OutboundCallReport>);

      const missingContactIds = contactIds.filter(id => !lastCallReports[id]);
      if (!missingContactIds.length) {
        setLastCallReports(callReports);
        return;
      }

      const { data } = await getOutboundContactsLastCallReport(missingContactIds);
      const reports = data.reduce((acc, report) => {
        acc[report.contact.id] = report;
        return acc;
      }, callReports);
      setLastCallReports(reports);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callTasks]);

  const hsPortalId = useMemo(() => {
    return (orgIntegrations.find(o => o.service === OrgIntegrationService.hubspot)
      ?.data as HubspotOrgIntegrationData)?.portalId;
  }, [orgIntegrations]);

  const emptyTitle =
    sourceType === undefined
      ? 'No source selected'
      : sourceType === SourceType.HUBSPOT_TASK
      ? 'No HubSpot Call Tasks to dial'
      : 'No Contacts in selected Contact List';
  const emptySubTitle =
    sourceType === undefined
      ? 'Please select a Source to load Tasks to dial'
      : sourceType === SourceType.HUBSPOT_TASK
      ? 'Create more Call Tasks in HubSpot or pick a Contact List'
      : 'Select a different Contact List or select Call Tasks';

  const rowStyler = (record: CallTask): string => {
    if (InProgressCallTaskStates.includes(record.status)) {
      return 'CallTaskDisplay__Table-row--dialing';
    }
    if (record.status === CallTaskStatus.SKIPPED) {
      return 'CallTaskDisplay__Table-row CallTaskDisplay__Table-row--skipped';
    }

    return 'CallTaskDisplay__Table-row';
  };

  const [properties, setProperties] = useState<{
    company: Property[];
    contact: Property[];
  }>({
    company: [],
    contact: [],
  });

  const fetchProperties = useCallback(async () => {
    Promise.all([getCompanyProperties(), getContactProperties()]).then(
      ([companyProperties, contactProperties]) => {
        setProperties({
          company: companyProperties.data,
          contact: contactProperties.data,
        });
      },
    );
  }, []);

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns: TableProps<CallTask>['columns'] = [
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: (a: CallTask, b: CallTask) =>
        statusOptions[a.status].rank - statusOptions[b.status].rank,
      sortDirections: ['descend', 'ascend'] as SortOrder[],
      render: (field: CallTaskStatus, record: CallTask) => {
        let status = field;

        if (!record?.contact?.phone) {
          status = CallTaskStatus.NO_NUMBER;
        }

        return <StatusBadge field={status} />;
      },
      ellipsis: true,
      width: 130,
    },
    {
      title: 'Name',
      dataIndex: ['contact', 'name'],
      render: (field: string, record: CallTask) => {
        const transcript =
          record?.id && InProgressCallTaskStates.includes(record.status)
            ? Object.values(transcriptions?.[record.id] ?? {})
                ?.sort((a, b) => a.startTime - b.startTime)
                .reverse()?.[0]
            : null;

        return (
          <CallTaskTooltip
            task={record}
            onSingleDialClick={onSingleDialClick}
            onStop={onStop}
            isDialing={isDialing}
          >
            <div className="CallTaskDisplay__name">
              <div className="CallTaskDisplay__nameWrap">
                <CallTaskName
                  {...{ field, record, onSingleDialClick, onStop, isDialing }}
                />
                <div className="CallTaskDisplay__socials">
                  {hsPortalId && sourceType?.includes('hubspot_') && (
                    <CallTaskSocialItem task={record} serviceName={'hubspot'} />
                  )}
                  {sourceType?.includes('outreach_') && (
                    <CallTaskSocialItem task={record} serviceName={'outreach'} />
                  )}
                  {sourceType?.includes('apollo_') && (
                    <CallTaskSocialItem task={record} serviceName={'apollo'} />
                  )}
                  {sourceType?.includes('salesloft_') && (
                    <CallTaskSocialItem task={record} serviceName={'salesloft'} />
                  )}
                  {sourceType?.includes('salesforce_') && (
                    <CallTaskSocialItem task={record} serviceName={'salesforce'} />
                  )}
                  <CallTaskSocialItem task={record} serviceName={'linkedin'} />
                  <CallTaskSocialItem task={record} serviceName={'google'} />
                </div>
              </div>
              {transcript && (
                <div className="CallTaskDisplay__transcript">
                  <Icon icon="textLong" width={12} height={12} />
                  <span>"{transcript.transcript}"</span>
                </div>
              )}
            </div>
          </CallTaskTooltip>
        );
      },
      width: 250,
      ellipsis: true,
    },
    {
      title: 'Phone',
      dataIndex: ['contact', 'phone'],
      render: (field, record) => {
        return (
          <PhoneField
            field={field}
            onSingleDialClick={onSingleDialClick}
            onInvalidNumberClick={onInvalidNumberClick}
            onStop={onStop}
            isDialing={isDialing}
            task={record}
          />
        );
      },
      width: 185,
      ellipsis: true,
    },
    {
      title: 'Last Activity',
      dataIndex: ['contact', 'id'],
      key: 'lastActivity',
      width: 300,
      render: (field: number, record: CallTask) => (
        <LastActivityField callReport={lastCallReports[field]} record={record} />
      ),
      ellipsis: true,
    },
    {
      title: 'Title',
      dataIndex: ['contact', 'title'],
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Time',
      dataIndex: ['contact', 'timezone'],
      width: 250,
      ellipsis: true,
      sorter: (a, b) => {
        const timeZoneA = a.contact.timezone ?? '';
        const timeZoneB = b.contact.timezone ?? '';
        const now = new Date();

        const timeA = utcToZonedTime(now, timeZoneA);
        const timeB = utcToZonedTime(now, timeZoneB);

        if (timeA < timeB) return -1;
        if (timeA > timeB) return 1;

        if (timeZoneA === '' && timeZoneB !== '') return 1;
        if (timeZoneA !== '' && timeZoneB === '') return -1;

        return 0;
      },
      render: field => {
        if (!field) return <></>;

        return <TimeField tz={field} />;
      },
    },
    {
      title: 'Company',
      dataIndex: ['contact', 'company', 'name'],
      width: 190,
      ellipsis: true,
    },
    {
      title: 'First seen',
      dataIndex: ['contact', 'company', 'firstVisitDate'],
      render: (field: number) => formatDateShort(field),
      width: 190,
      ellipsis: true,
    },
    {
      title: 'Last seen',
      dataIndex: ['contact', 'company', 'lastVisitDate'],
      render: (field: number) => formatDateShort(field),
      width: 190,
      ellipsis: true,
    },
    {
      title: 'Country',
      dataIndex: ['contact', 'company', 'country'],
      width: 190,
      ellipsis: true,
    },
    {
      title: 'Location',
      dataIndex: ['contact', 'company', 'location'],
      width: 190,
      ellipsis: true,
    },
    {
      title: 'Company Website',
      dataIndex: ['contact', 'company', 'domain'],
      render: (field: string) => {
        if (!field) return <></>;
        return (
          <a
            href={!field?.startsWith('https://') ? `https://${field}` : field}
            target="_blank"
            rel="noreferrer"
          >
            {field}
          </a>
        );
      },
      width: 190,
      ellipsis: true,
    },
    {
      title: 'Company Size',
      dataIndex: ['contact', 'company', 'sizeEstimateHigh'],
      width: 190,
      ellipsis: true,
      render: (_field: number, record: CallTask) =>
        record?.contact?.company?.sizeEstimateLow ==
        record?.contact?.company?.sizeEstimateHigh
          ? record?.contact?.company?.sizeEstimateLow
          : `${record?.contact?.company?.sizeEstimateLow}-${record?.contact?.company?.sizeEstimateHigh}`,
    },
    {
      title: 'Est. Revenue',
      dataIndex: ['contact', 'company', 'revenue'],
      width: 190,
      ellipsis: true,
      render: (revenue: number) => (revenue ? revenueFormatter.format(revenue) : null),
    },
    {
      title: 'UTM Source',
      dataIndex: ['contact', 'company', 'utmSource'],
      width: 190,
      ellipsis: true,
    },
    {
      title: 'UTM Medium',
      width: 190,
      dataIndex: ['contact', 'company', 'utmMedium'],
      ellipsis: true,
    },
    {
      title: 'UTM Campaign',
      width: 190,
      dataIndex: ['contact', 'company', 'utmCampaign'],
      ellipsis: true,
    },
    {
      title: 'UTM Content',
      width: 190,
      dataIndex: ['contact', 'company', 'utmContent'],
      ellipsis: true,
    },
    {
      title: 'UTM Term',
      dataIndex: ['contact', 'company', 'utmTerm'],
      width: 190,
      ellipsis: true,
    },
    {
      title: 'Industry',
      dataIndex: ['contact', 'company', 'industry'],
      width: 190,
      ellipsis: true,
    },
    { title: 'Email', dataIndex: ['contact', 'email'], width: 190, ellipsis: true },
    { title: 'Source', dataIndex: ['contact', 'source'], width: 190, ellipsis: true },
    {
      title: 'Stats',
      dataIndex: ['stats'],
      render: stats => (stats ? `${stats.pickups}/${stats.dials}` : ''),
      width: '190px',
      ellipsis: true,
    },
  ];

  const orderedColumns = useMemo(() => {
    return [
      ...columnsToRender
        .map(col => {
          /* @ts-ignore */
          const res = columns.find(c => [c?.dataIndex].flat().join('.') === col.property);

          if (!res) {
            if (!col.enabled || ['skip', 'links'].includes(col.property)) {
              return null;
            }

            const group = startsWith(col.property, 'contact.company')
              ? 'company'
              : 'contact';
            const split = col.property.split('.');
            const property = properties?.[group as 'company' | 'contact']?.find(
              p => p.key === split[split?.length - 1] ?? '',
            );
            const title = property?.displayName;

            return {
              title: title,
              dataIndex: col.property?.split('.'),
              key: col.property,
              width: 190,
              elllipsis: true,
              render: (field: any) => {
                if (property?.type === 'user') {
                  const user = activeUsers.find(u => u.id === field);
                  return user?.emailAddress || '';
                }
                return field;
              },
            };
          }
          return res;
        })
        .filter(c => {
          if (['skip', 'links'].includes(c?.key as string)) return true;

          return columnsToRender.find(
            /* @ts-ignore */
            col => col.property === [c?.dataIndex].flat().join('.'),
          )?.enabled;
        }),
      {
        title: 'Skip',
        dataIndex: ['contact', 'id'],
        key: 'skip',
        fixed: 'right' as any,
        width: 72,
        sorter: (a: CallTask, b: CallTask) => {
          return (
            Number(a.status === CallTaskStatus.SKIPPED) -
            Number(b.status === CallTaskStatus.SKIPPED)
          );
        },
        sortDirections: ['descend', 'ascend'] as SortOrder[],
        render: (_field: number, record: CallTask) => {
          return (
            <Tooltip title={'Skip this call'} theme={'dark'}>
              <Switch
                className="CallTaskDisplay__switch"
                size="small"
                onChange={async () => {
                  await toggleSkipped(record);
                }}
                checked={record.status === CallTaskStatus.SKIPPED}
                disabled={
                  ![CallTaskStatus.PENDING, CallTaskStatus.SKIPPED].includes(
                    record.status,
                  )
                }
              />
            </Tooltip>
          );
        },
      },
    ];
  }, [activeUsers, columns, columnsToRender, properties, toggleSkipped]);

  const handleReachedEnd = useCallback(async () => {
    setLoading(true);
    await loadMoreData();
    setLoading(false);
  }, [loadMoreData]);

  const [activeTasks, actionedTasks] = useMemo(() => {
    return callTasks.reduce(
      ([active, actioned]: Array<Array<CallTask>>, task) => {
        const isCompleted = CompletedCallTaskStates.includes(task.status);

        if (isCompleted) {
          actioned.push(task);
        } else {
          active.push(task);
        }
        return [active, actioned];
      },
      [[], []],
    );
  }, [callTasks]);

  useEffect(() => {
    if (!actionedTasks.length) {
      setCompletedOpen(false);
    }
  }, [actionedTasks]);

  const virtualListScroll = window.innerHeight - 74 - 90 - 73 - 60 - 48 - 50;

  return (
    <>
      <div className="CallTaskDisplay">
        {isLoading ? (
          <div className="CallTaskDisplay__Empty">
            <CallTaskDisplaySkeleton />
          </div>
        ) : activeTasks?.length === 0 ? (
          <div className="CallTaskDisplay__Empty">
            <BellMessage type="empty" title={emptyTitle} text={emptySubTitle} />
          </div>
        ) : (
          <>
            <div className="CallTaskDisplay__Table">
              <div className="CallTaskDisplay__Table-body">
                <Table
                  rowKey="frontendId"
                  rowClassName={rowStyler}
                  dataSource={activeTasks}
                  columns={orderedColumns as TableProps<CallTask>['columns']}
                  pagination={false}
                  loading={loading}
                  scroll={{ y: virtualListScroll, scrollToFirstRowOnChange: false }}
                  components={{
                    body: {
                      wrapper: ({
                        children,
                        ...props
                      }: React.HTMLAttributes<HTMLTableSectionElement>) => (
                        <InfiniteScrollTableWrapper
                          onReachedEnd={cursor !== null ? handleReachedEnd : null}
                          {...props}
                        >
                          {children}
                        </InfiniteScrollTableWrapper>
                      ),
                    },
                  }}
                  sticky
                />
              </div>
            </div>
          </>
        )}
      </div>
      {!!callTasks.length && (
        <Collapse
          className="CallTaskDisplay__collapse"
          activeKey={completedOpen ? 'actioned' : undefined}
          onChange={keys => setCompletedOpen(!!keys.length)}
        >
          <Collapse.Panel
            collapsible={!actionedTasks.length ? 'disabled' : undefined}
            showArrow={false}
            header={
              <>
                <Icon icon="chevron" width={16} height={16} />
                <div>
                  Completed tasks <span>({actionedTasks.length})</span>
                </div>
              </>
            }
            key={'actioned'}
            className="CallTaskDisplay__collapse-panel"
          >
            <AutoSizer
              className="CallTaskDisplay__collapse-body"
              disableHeight
              disableWidth
            >
              {() => {
                return (
                  <Table
                    rowKey="contact.id"
                    rowClassName={rowStyler}
                    dataSource={actionedTasks}
                    columns={
                      orderedColumns.filter(
                        c => c?.key !== 'skip',
                      ) as TableProps<CallTask>['columns']
                    }
                    pagination={false}
                    scroll={{ y: 300, scrollToFirstRowOnChange: false }}
                    loading={loading}
                    sticky
                  />
                );
              }}
            </AutoSizer>
          </Collapse.Panel>
        </Collapse>
      )}
    </>
  );
};

const CallTaskName: React.FC<{
  field: string;
  record: CallTask;
  onSingleDialClick: (task: CallTask) => Promise<void>;
  onStop: () => Promise<void>;
  isDialing?: boolean;
}> = ({ field, record }) => {
  let name = record?.contact?.name ?? '';
  if (name === '') {
    name = record?.contact?.email ?? '';
  }
  if (name === '') {
    name = '(Unknown)';
  }

  return (
    <div
      className={clsx(
        'OutboundCallTaskName__name',
        !field && 'OutboundCallTaskName__name--email',
      )}
    >
      {name}
    </div>
  );
};

export const StatusBadge: React.FC<{ field: string }> = ({ field }) => {
  const badgeStyle =
    field in statusOptions
      ? {
          backgroundColor: statusOptions[field].color,
          border: statusOptions[field]?.border
            ? `1px solid ${statusOptions[field]?.border}`
            : 'unset',
          color: statusOptions[field].invert ? '#2C2D36' : '#FFFFFF',
        }
      : {
          backgroundColor: '#F2F4F7',
          border: '1px solid #E7EAF2',
          color: '#2C2D36',
        };

  if (statusOptions[field]?.tooltip) {
    return (
      <Tooltip title={statusOptions[field].tooltip} theme={'dark'}>
        <div className="OutboundStatusBadge">
          <div className="OutboundStatusBadge__Badge" style={badgeStyle}>
            {!!statusOptions[field]?.icon && (
              <Icon
                width={12}
                height={12}
                icon={statusOptions[field]?.icon as IconName}
              />
            )}
            {getCallStatusFriendlyName(field as CallTaskStatus) ?? field}
          </div>
        </div>
      </Tooltip>
    );
  }

  return (
    <div className="OutboundStatusBadge">
      <div className="OutboundStatusBadge__Badge" style={badgeStyle}>
        {!!statusOptions[field]?.icon && (
          <Icon width={12} height={12} icon={statusOptions[field]?.icon as IconName} />
        )}
        {getCallStatusFriendlyName(field as CallTaskStatus) ?? field}
      </div>
    </div>
  );
};

const Separator: React.FC = () => <span>·</span>;

export const LastActivityField: React.FC<{
  callReport?: OutboundCallReport;
  record: CallTask;
}> = ({ callReport, record }) => {
  const {
    apolloCallDispositions,
    hubspotCallDispositions,
    outreachCallDispositions,
    salesforceCallDispositions,
    salesloftCallDispositions,
    servicebellCallDispositions,
  } = useAppSelector(state => state.outbound);
  const users = useAppSelector(selectActiveOrgMembers);
  const user = useMemo(() => {
    return users.find(user => user.id === callReport?.userId);
  }, [callReport?.userId, users]);

  const callDisposition = useMemo(() => {
    return (
      callReport &&
      getActivityCallDisposition(
        callReport,
        apolloCallDispositions,
        hubspotCallDispositions,
        outreachCallDispositions,
        salesforceCallDispositions,
        salesloftCallDispositions,
        servicebellCallDispositions,
      )
    );
  }, [
    callReport,
    apolloCallDispositions,
    hubspotCallDispositions,
    outreachCallDispositions,
    salesforceCallDispositions,
    salesloftCallDispositions,
    servicebellCallDispositions,
  ]);

  if (!callReport) {
    return <></>;
  }
  const timestamp = new Date(`${callReport.dateCreated}Z`);
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setFullYear(cutoff.getFullYear() - 1, cutoff.getMonth(), cutoff.getDate() + 1);
  const dateFormat = `LLL do${timestamp < cutoff ? ', yyyy' : ''}`;

  return (
    <div className="OutboundLastActivityField__wrapper">
      <div
        className={clsx(
          'OutboundLastActivityField',
          InProgressCallTaskStates.includes(record.status) &&
            'OutboundLastActivityField--dialing',
        )}
      >
        <div className="OutboundLastActivityField__summary">
          <StatusBadge field={callDisposition!} />
          <span />
          <span className="OutboundLastActivityField__date">
            {format(timestamp, dateFormat)}
          </span>
          <Separator />
          <span className="OutboundLastActivityField__time">
            {format(timestamp, 'h:mm a')}
          </span>
          {user && (
            <>
              <Separator />
              <span className="OutboundLastActivityField__user">{user.displayName}</span>
            </>
          )}
        </div>
        <div className="OutboundLastActivityField__notes">{callReport.notes}</div>
      </div>
    </div>
  );
};

export const PhoneField: React.FC<{
  field: string;
  onSingleDialClick: (task: CallTask) => Promise<void>;
  onInvalidNumberClick: (task: CallTask, number: string) => Promise<void>;
  onStop: () => Promise<void>;
  isDialing?: boolean;
  task: CallTask;
}> = ({ field, onSingleDialClick, onInvalidNumberClick, onStop, isDialing, task }) => {
  const disabledPhoneNumbers = useMemo(() => {
    const invalidNumbers = task?.invalidNumbers ?? [];
    const attemptedNumbers = task?.attemptedNumbers ?? [];
    return [...invalidNumbers, ...attemptedNumbers];
  }, [task?.invalidNumbers, task?.attemptedNumbers]);
  const { filters } = useSelector((state: AppState) => state.outbound);

  const getNumberStatusReason = useCallback(
    (p: LBBContactPhoneNumber) => {
      if (!p.isValid) {
        return 'Number is invalid';
      }
      if (p.doNotCall) {
        return 'National Do Not Call registry';
      }
      if (p.regionCode !== 'US') {
        return `Region code ${p.regionCode} is not US`;
      }
      if (filters.numberTypes.length && !filters.numberTypes.includes(p.kind)) {
        return `Number type “${p.kind.substring(0, 1)}${p.kind
          .substring(1)
          .toLowerCase()}” is not selected in filters`;
      }

      return 'Dialed unsuccessfully';
    },
    [filters],
  );

  if (!field)
    return (
      <div className="OutboundPhoneField">
        <div className="OutboundPhoneField__phone">
          <span className="OutboundPhoneField__placeholder" />
        </div>
      </div>
    );

  const phoneNumbers = task.contact.phoneNumbers;

  if (CompletedCallTaskStates.includes(task.status)) {
    return (
      <div className="OutboundPhoneField">
        {phoneNumbers.map((p, index) => {
          const field = (
            <div
              className={clsx(
                'OutboundPhoneField__phone',
                index === 0 && 'OutboundPhoneField__phone--current',
              )}
              key={`${p.number}-${index}`}
            >
              {index > 0 && <Icon icon="arrowCurveRight" />}
              <Icon icon={getPhoneNumberIcon(p)} />
              <div className="OutboundPhoneField__Text">
                <span
                  style={
                    disabledPhoneNumbers?.includes(p.number)
                      ? { textDecoration: 'line-through' }
                      : {}
                  }
                >
                  {p.nationalNumber ?? p.number}
                </span>
              </div>
            </div>
          );

          return disabledPhoneNumbers.includes(p.number) ? (
            <Tooltip
              key={`${p.number}-${index}`}
              title={getNumberStatusReason(p)}
              theme="dark"
              placement="right"
            >
              {field}
            </Tooltip>
          ) : (
            field
          );
        })}
      </div>
    );
  }

  return (
    <div className="OutboundPhoneField">
      {phoneNumbers.map((p, index) => {
        return (
          <Popover
            key={`${p?.number}-${index}`}
            trigger="hover"
            content={
              <div className="OutboundPhoneField__call-wrapper">
                {disabledPhoneNumbers.includes(p.number) ? (
                  <div className="OutboundPhoneField__invalid-reason">
                    <Icon icon="block" />
                    {getNumberStatusReason(p)}
                  </div>
                ) : (
                  <>
                    <Button
                      className={clsx(
                        `OutboundPhoneField__call`,
                        isDialing && `OutboundPhoneField__call--cancel`,
                      )}
                      type="primary"
                      size="middle"
                      onClick={async () =>
                        await (isDialing ? onStop() : onSingleDialClick(task))
                      }
                    >
                      <Icon icon="phoneOn" />
                      {!isDialing ? 'Call' : 'Cancel'}
                    </Button>
                    <Button
                      className="OutboundPhoneField__invalid"
                      type="primary"
                      size="middle"
                      onClick={async () => await onInvalidNumberClick(task, p.number)}
                    >
                      <Icon icon="block" />
                      Invalid
                    </Button>
                  </>
                )}
              </div>
            }
            placement="right"
            overlayClassName="OutboundPhoneField__popover"
          >
            <div
              className={clsx(
                'OutboundPhoneField__phone',
                index === 0 && 'OutboundPhoneField__phone--current',
              )}
            >
              {index > 0 && <Icon icon="arrowCurveRight" />}
              <Icon icon={getPhoneNumberIcon(p)} />
              <div
                className={clsx(
                  `OutboundPhoneField__Text`,
                  disabledPhoneNumbers.includes(p.number) &&
                    `OutboundPhoneField__Text--disabled`,
                )}
              >
                {p.nationalNumber ?? p.number}
              </div>
            </div>
          </Popover>
        );
      })}
    </div>
  );
};

const TimeField: React.FC<{ tz?: string }> = ({ tz }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update the time every second (1000 milliseconds)

    return () => {
      clearInterval(intervalId); // Clean up the interval when the component unmounts
    };
  }, []);

  if (!tz) return <></>;

  return (
    <>
      {format(utcToZonedTime(currentTime, tz), 'h:mm a')} ({tz})
    </>
  );
};
